import React from "react";

import { SettingsPageContainer, useToast } from "../../../components";
import { isCustomError } from "../../../utils/apollo/error";
import { useSendGAEvent } from "../../../utils/googleAnalytics";
import { CallVisibility, CurrentUserFragment } from "../../graphql";
import useUpdateCurrentUser from "../../graphql/hooks/useUpdateCurrentUser";
import UserSettingsForm, { FormData } from "./UserSettingsForm";

interface UserSettingsProps {
  currentUser: CurrentUserFragment;
}

const UserSettings: React.FC<UserSettingsProps> = ({ currentUser }) => {
  const toast = useToast();
  const sendGAEvent = useSendGAEvent();
  const [updateCurrentUser, { loading: updateLoading, error }] =
    useUpdateCurrentUser({
      onCompleted: (user) => {
        if (user) {
          sendGAEvent("updated", "user_settings");
          toast({
            title: "Settings saved",
            status: "success",
          });
        }
      },
      onError: (error) => {
        toast({
          title: "Error updating user",
          description: isCustomError(error) ? error.message : undefined,
          status: "error",
        });
      },
    });

  const onSubmit = (formData: FormData): void => {
    updateCurrentUser({
      variables: formData,
    });
  };

  return (
    <SettingsPageContainer
      heading="Profile"
      subHeading="View or update your personal information"
    >
      <UserSettingsForm
        firstName={currentUser.firstName ?? ""}
        lastName={currentUser.lastName ?? ""}
        timezoneName={currentUser.timezoneName}
        emailAliases={currentUser.emailAliases}
        persona={currentUser.persona}
        error={error}
        isLoading={updateLoading}
        onSubmit={onSubmit}
        phoneNumber={currentUser.phoneNumber ?? ""}
        phoneNumberExtension={currentUser.phoneNumberExtension ?? ""}
        maskPersonalPhoneNumber={currentUser.maskPersonalPhoneNumber ?? ""}
        defaultCallVisibility={
          currentUser.defaultCallVisibility ?? CallVisibility.Public
        }
      />
    </SettingsPageContainer>
  );
};

export default UserSettings;
